import Fuse from 'fuse.js';
import { ChangeEvent, PureComponent, ReactNode } from 'react';
import { GTreatmentPackage, GTreatmentService } from '../../store/db';
import {
    getDoc,
    getGArrayWithClinicId,
    getMe,
    getSelectedClinicId,
    upsertTreatmentPackage,
} from '../../store/idbHandler';
import { DB_TABLES } from '../../store/dbTables';
import { getMyDbId } from '../../api/mactiveid';
import { captureError } from '../../pages/captureError';
import { Dropdown, Menu, Popover, Switch, notification } from 'antd';
import Table, { ColumnGroupType, ColumnType } from 'antd/lib/table';
import EmptyView from './EmptyView';
import CreateOrEditTreatmentPackage from './CreateOrEditTreatmentPackage';
import createTreatmentPackage from '../../api/createTreatmentPackage';
import { clinicIcon } from '../common/Icons';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { fetchDoc } from '../../store/actions/login';
import { ThunkDispatch } from 'redux-thunk';
import { GlobalState } from '../../store/reducers';
import { Actions } from '../../store/actions';
import { connect } from 'react-redux';
import { formattedNumber } from '../../pages/utils';

const fuseConfig = {
    keys: ['_name', '_services'],
    threshold: 0.2,
    ignoreLocation: true,
    useExtendedSearch: true,
};

interface Clinic {
    _id: string;
    name: string;
}

interface Props extends RouteComponentProps {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
}

interface State {
    isFetchingTreatmentPackages: boolean;
    treatmentPackages: GTreatmentPackage[];
    searchFilter?: string;
    isCreateTreatmentPackageModalVisible?: boolean;
    editTreatmentPackage?: GTreatmentPackage;
    clinics: Clinic[];
    selectedClinicId?: string;
}

class ManageTreatmentPackages extends PureComponent<Props, State> {
    treatmentPackagesFuse = new Fuse([] as GTreatmentPackage[], fuseConfig);
    doctorProfile = getDoc()?.profile;
    me = getMe();

    initialState: State = {
        isFetchingTreatmentPackages: true,
        treatmentPackages: [],
        clinics: [],
    };

    state: State = this.initialState;

    componentDidMount = async () => {
        const { dispatch } = this.props;
        const { doctorProfile, fetchTreatmentPackages, me } = this;
        const { professional } = doctorProfile || {};
        const clinics =
            professional?.clinics || me?.clinics?.map((c) => ({ ...c, _id: c.id })) || [];
        const selectedClinicId = getSelectedClinicId() || '';
        this.setState({
            clinics: clinics,
            selectedClinicId: selectedClinicId,
        });
        const fetchTreatmentPackagesFunc = () => fetchTreatmentPackages(selectedClinicId);
        const docId = getDoc()?._id || '';
        dispatch(fetchDoc(docId, fetchTreatmentPackagesFunc, fetchTreatmentPackagesFunc));
    };

    fetchTreatmentPackages = async (clinic_id: string) => {
        const { setupTreatmentPackagesFuse } = this;
        this.setState({
            isFetchingTreatmentPackages: true,
            treatmentPackages: [],
        });
        try {
            const gTreatmentPackages = await getGArrayWithClinicId<GTreatmentPackage>(
                getMyDbId() || '',
                DB_TABLES['treatmentPackages'],
                clinic_id,
            );
            this.setState({
                isFetchingTreatmentPackages: false,
                treatmentPackages: gTreatmentPackages.sort((a, b) => {
                    const dateA = a.updated_at ? new Date(a.updated_at) : null;
                    const dateB = b.updated_at ? new Date(b.updated_at) : null;
                    return (dateB as any) - (dateA as any);
                }),
            });
            setupTreatmentPackagesFuse(gTreatmentPackages);
        } catch (err) {
            captureError(err as Error);
            notification.error({
                message: 'Error while fetching treatment packages',
                duration: 5,
            });
            this.setState({
                isFetchingTreatmentPackages: false,
            });
        }
    };

    setupTreatmentPackagesFuse = (treatmentPackages: GTreatmentPackage[]): void => {
        this.treatmentPackagesFuse = new Fuse(
            treatmentPackages.map((t) => ({
                ...t,
                _name: t.name,
                _services: t.services.map((i) => i.name).join(', '),
            })),
            fuseConfig,
        );
    };

    renderComponent = (treatmentPackages: GTreatmentPackage[]) => {
        const { isFetchingTreatmentPackages } = this.state;
        const { onEditTreatmentPackage } = this;

        const columns: (ColumnGroupType<GTreatmentPackage> | ColumnType<GTreatmentPackage>)[] = [
            {
                title: (
                    <span className="font-700 text-12 text-darwin-neutral-700 uppercase">
                        S. No.
                    </span>
                ),
                width: 60,
                key: 'index',
                render: (_: GTreatmentPackage, __: GTreatmentPackage, index: number): ReactNode => {
                    return (
                        <p className="text-14 text-darwin-neutral-900 whitespace-nowrap">
                            {index + 1}
                        </p>
                    );
                },
                fixed: 'left',
            },
            {
                title: (
                    <span className="font-700 text-12 text-darwin-neutral-700 uppercase">
                        Treatment Package Name
                    </span>
                ),
                width: 180,
                key: 'name',
                dataIndex: 'name',
                render: (name: string): ReactNode => {
                    return <p>{name}</p>;
                },
                sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
            },
            {
                title: (
                    <span className="font-700 text-12 text-darwin-neutral-700 uppercase">
                        Services
                    </span>
                ),
                width: 250,
                key: 'services',
                dataIndex: 'services',
                render: (
                    services: GTreatmentService[],
                    treatmentPackage: GTreatmentPackage,
                ): ReactNode => {
                    const displayedServices = services
                        .slice(0, 2)
                        .map((i) => i.name)
                        .join(', ');
                    const remainingCount = services.length - 2;
                    return (
                        <Popover
                            placement="bottom"
                            title={
                                <p className="font-700 select-none">
                                    Services included in this package ({services.length})
                                </p>
                            }
                            content={
                                <div className="space-y-4 select-none overflow-auto max-h-328 max-w-400">
                                    {services.map((service, index) => (
                                        <p className="flex items-start space-x-8">
                                            <p>{index + 1}.</p>
                                            <div className="flex-1 flex items-start justify-between">
                                                <p className="flex-1">{service.name}</p>
                                                <p className="text-right w-80">₹{service.price}</p>
                                            </div>
                                        </p>
                                    ))}
                                </div>
                            }
                            trigger="hover"
                        >
                            <p className="cursor-pointer" id={treatmentPackage._id}>
                                {remainingCount > 0 ? (
                                    <>
                                        {displayedServices},{' '}
                                        <span className="text-darwin-neutral-600">
                                            +{remainingCount} more
                                        </span>
                                    </>
                                ) : (
                                    displayedServices
                                )}
                            </p>
                        </Popover>
                    );
                },
                sorter: (a, b) =>
                    (a.services.join(', ') || '').localeCompare(b.services.join(', ') || ''),
            },
            {
                title: (
                    <span className="font-700 text-12 text-darwin-neutral-700 uppercase">
                        Price
                    </span>
                ),
                width: 70,
                key: 'price',
                dataIndex: 'price',
                render: (price: number): ReactNode => {
                    return <p>₹{formattedNumber(price)}</p>;
                },
                sorter: ({ price: a = 0 }, { price: b = 0 }) => a - b,
            },
            {
                title: (
                    <p className="w-full text-right font-700 text-12 text-darwin-neutral-700 uppercase pr-4">
                        Active/Inactive
                    </p>
                ),
                width: 120,
                key: 'archive',
                dataIndex: 'archive',
                render: (archive: boolean, treatmentPackage: GTreatmentPackage): ReactNode => {
                    return (
                        <p className="w-full text-right flex justify-end pr-4">
                            <Switch
                                className="link-color"
                                checked={!archive}
                                onChange={() => this.onChangeStatus(treatmentPackage)}
                                style={{
                                    backgroundColor: !archive ? '#19A66A' : 'rgba(0,0,0,.25)',
                                }}
                            />
                        </p>
                    );
                },
                fixed: 'right',
            },
            {
                title: (
                    <p className="w-full text-right font-700 text-12 text-darwin-neutral-700 uppercase pr-4">
                        Actions
                    </p>
                ),
                width: 70,
                key: 'action',
                render: (_: GTreatmentPackage, treatmentPackage: GTreatmentPackage): ReactNode => {
                    return (
                        <p className="w-full text-right flex justify-end pr-4">
                            <svg
                                className="w-14 h-14 fill-darwin-accent-bright-blue-800 cursor-pointer"
                                viewBox="0 0 14 14"
                                xmlns="http://www.w3.org/2000/svg"
                                onClick={() => onEditTreatmentPackage(treatmentPackage)}
                            >
                                <path d="M11.6973 1.62305H11.6719C11.4434 1.39453 11.0625 1.39453 10.834 1.62305L10.0723 2.35938L11.2656 3.55273L12.002 2.79102C12.2305 2.5625 12.2305 2.18164 12.002 1.92773L11.6973 1.62305ZM5.80664 6.65039C5.73047 6.70117 5.67969 6.80273 5.6543 6.9043L5.22266 8.37695L6.7207 7.9707C6.82227 7.94531 6.89844 7.89453 6.97461 7.81836L10.4023 4.39062L9.23438 3.22266L5.80664 6.65039ZM9.9707 0.759766C10.6816 0.0488281 11.8242 0.0488281 12.5352 0.759766L12.8652 1.08984C13.5762 1.80078 13.5762 2.94336 12.8652 3.6543L7.83789 8.68164C7.63477 8.88477 7.35547 9.03711 7.05078 9.13867L4.51172 9.84961C4.30859 9.92578 4.08008 9.875 3.92773 9.69727C3.75 9.54492 3.69922 9.31641 3.75 9.11328L4.48633 6.57422C4.5625 6.26953 4.74023 5.99023 4.94336 5.78711L9.9707 0.759766ZM2.73438 1.75H5.57812C5.9082 1.75 6.1875 2.0293 6.1875 2.35938C6.1875 2.71484 5.9082 2.96875 5.57812 2.96875H2.73438C2.15039 2.96875 1.71875 3.42578 1.71875 3.98438V10.8906C1.71875 11.4746 2.15039 11.9062 2.73438 11.9062H9.64062C10.1992 11.9062 10.6562 11.4746 10.6562 10.8906V8.04688C10.6562 7.7168 10.9102 7.4375 11.2656 7.4375C11.5957 7.4375 11.875 7.7168 11.875 8.04688V10.8906C11.875 12.1348 10.8594 13.125 9.64062 13.125H2.73438C1.49023 13.125 0.5 12.1348 0.5 10.8906V3.98438C0.5 2.76562 1.49023 1.75 2.73438 1.75Z" />
                            </svg>
                        </p>
                    );
                },
                fixed: 'right',
            },
        ];

        return isFetchingTreatmentPackages ? (
            <div className="h-full flex items-center justify-center">
                <svg
                    className="w-24 h-24 fill-darwin-accent-bright-blue-800 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                >
                    <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                </svg>
            </div>
        ) : treatmentPackages.length ? (
            <Table
                columns={columns}
                dataSource={treatmentPackages}
                pagination={false}
                sticky={true}
                scroll={{
                    x: 1000,
                }}
            />
        ) : (
            <EmptyView />
        );
    };

    onEditTreatmentPackage = (editTreatmentPackage: GTreatmentPackage) => {
        this.setState({
            isCreateTreatmentPackageModalVisible: true,
            editTreatmentPackage: editTreatmentPackage,
        });
    };

    onSearchInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
        this.setState({
            searchFilter: e.target.value,
        });
    };

    clearSearchFilter = (): void => {
        this.setState({
            searchFilter: '',
        });
    };

    createTreatmentPackageModalToggleHandler = (refresh?: boolean) => {
        const { isCreateTreatmentPackageModalVisible } = this.state;
        const { fetchTreatmentPackages } = this;

        this.setState({
            isCreateTreatmentPackageModalVisible: !isCreateTreatmentPackageModalVisible,
        });

        if (isCreateTreatmentPackageModalVisible) {
            this.setState({
                editTreatmentPackage: undefined,
            });
        }

        if (refresh) {
            const { selectedClinicId } = this.state;
            fetchTreatmentPackages(selectedClinicId || '');
        }
    };

    onChangeStatus = async (treatmentPackage: GTreatmentPackage) => {
        const { treatmentPackages } = this.state;
        const { _id, archive } = treatmentPackage;
        const newTreatmentPackages = treatmentPackages.map((i) =>
            i._id === _id ? { ...i, archive: !i.archive } : i,
        );
        this.setState({
            treatmentPackages: Array.from(newTreatmentPackages),
        });
        const newTreatmentPackage = {
            ...treatmentPackage,
            archive: !archive,
            updated_at: new Date().toISOString(),
        };
        try {
            await upsertTreatmentPackage(newTreatmentPackage);
            const { error } = await createTreatmentPackage(newTreatmentPackage);
            if (error) {
                notification.error({
                    message: 'Error while saving treatment package',
                    duration: 5,
                });
            }
        } catch (err) {
            captureError(err as Error);
            notification.error({
                message: 'Error while saving treatment package',
                duration: 5,
            });
        }
    };

    onClinicSelect = (selectedClinicId?: string) => {
        const { selectedClinicId: oldSelectedClinicId } = this.state;
        if (oldSelectedClinicId === selectedClinicId) return;
        const { fetchTreatmentPackages } = this;
        this.setState({
            selectedClinicId: selectedClinicId,
        });
        fetchTreatmentPackages(selectedClinicId || '');
    };

    render(): ReactNode {
        const {
            treatmentPackages,
            searchFilter,
            isCreateTreatmentPackageModalVisible,
            editTreatmentPackage,
            clinics,
            selectedClinicId,
        } = this.state;
        const {
            renderComponent,
            treatmentPackagesFuse,
            onSearchInputChange,
            clearSearchFilter,
            createTreatmentPackageModalToggleHandler,
            onClinicSelect,
        } = this;

        let filteredTreatmentPackages: GTreatmentPackage[] = treatmentPackages;
        if (searchFilter) {
            filteredTreatmentPackages = treatmentPackagesFuse
                .search({
                    $or: [{ _name: searchFilter }, { _services: searchFilter }],
                })
                .map((i) => i.item);
        }

        return (
            <div className="w-full h-full flex flex-col p-24 space-y-16 overflow-x-auto overflow-y-hidden">
                <div className="flex items-center justify-between">
                    <p className="text-16 font-700">Treatment packages</p>
                    <div className="flex items-center space-x-12">
                        <div className="flex gap-8 items-center">
                            <div
                                className={
                                    'flex rounded-8 items-center h-36 ml-8 justify-between px-8 bg-darwin-neutral_new-50 border-neutral-5 rounded-4 text-14 w-200'
                                }
                            >
                                <Dropdown
                                    overlay={
                                        <Menu className="flex flex-col rounded-8 p-0">
                                            {clinics?.map((c, i) => {
                                                return (
                                                    <Menu.Item
                                                        key={c._id}
                                                        onClick={() => {
                                                            const input = c._id;
                                                            onClinicSelect(input);
                                                        }}
                                                        className={`${
                                                            i + 1 !== clinics.length
                                                                ? 'border-b-1 border-neutral-7'
                                                                : ''
                                                        } ${i === 0 && 'rounded-t-8'}
                                        ${i + 1 === clinics.length && 'rounded-b-8'}`}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <span>{c.name}</span>
                                                        </div>
                                                    </Menu.Item>
                                                );
                                            })}
                                        </Menu>
                                    }
                                >
                                    <div
                                        className="w-full bg-neutral-9 items-center justify-between cursor-pointer"
                                        style={{ display: 'flex' }}
                                    >
                                        <div className="flex items-center gap-8">
                                            {clinicIcon('w-24 h-24')}
                                            <p className="overflow-ellipsis text-13 overflow-hidden whitespace-nowrap w-140">
                                                {clinics?.find((c) => c._id === selectedClinicId)
                                                    ?.name ?? ''}
                                            </p>
                                        </div>
                                        <div className="flex items-center space-x-4">
                                            <span className="w-16 h-16 min-w-16">
                                                <svg
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 16 16"
                                                    className="w-16 h-16 min-w-16"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M13.2812 6.45703L8.43164 11.1035C8.2793 11.2305 8.12695 11.2812 8 11.2812C7.84766 11.2812 7.69531 11.2305 7.56836 11.1289L2.69336 6.45703C2.43945 6.22852 2.43945 5.82227 2.66797 5.59375C2.89648 5.33984 3.30273 5.33984 3.53125 5.56836L8 9.83398L12.4434 5.56836C12.6719 5.33984 13.0781 5.33984 13.3066 5.59375C13.5352 5.82227 13.5352 6.22852 13.2812 6.45703Z"
                                                        fill="#1A1A1A"
                                                    />
                                                </svg>
                                            </span>
                                        </div>
                                    </div>
                                </Dropdown>
                            </div>
                        </div>
                        <div className="relative flex items-center space-x-5 border border-darwin-neutral_new-200 px-12 py-7 rounded-8">
                            <input
                                type="text"
                                placeholder="Search by package name"
                                className="text-13"
                                onChange={onSearchInputChange}
                                value={searchFilter}
                            />
                            {searchFilter ? (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 512 512"
                                    className="w-14 h-14 fill-darwin-neutrals-600 cursor-pointer"
                                    onClick={clearSearchFilter}
                                >
                                    <path d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm121.6 313.1c4.7 4.7 4.7 12.3 0 17L338 377.6c-4.7 4.7-12.3 4.7-17 0L256 312l-65.1 65.6c-4.7 4.7-12.3 4.7-17 0L134.4 338c-4.7-4.7-4.7-12.3 0-17l65.6-65-65.6-65.1c-4.7-4.7-4.7-12.3 0-17l39.6-39.6c4.7-4.7 12.3-4.7 17 0l65 65.7 65.1-65.6c4.7-4.7 12.3-4.7 17 0l39.6 39.6c4.7 4.7 4.7 12.3 0 17L312 256l65.6 65.1z" />
                                </svg>
                            ) : (
                                <svg
                                    className="w-14 h-14 fill-darwin-neutrals-600"
                                    viewBox="0 0 17 17"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M11.5 6.5C11.5 4.71875 10.5312 3.09375 9 2.1875C7.4375 1.28125 5.53125 1.28125 4 2.1875C2.4375 3.09375 1.5 4.71875 1.5 6.5C1.5 8.3125 2.4375 9.9375 4 10.8438C5.53125 11.75 7.4375 11.75 9 10.8438C10.5312 9.9375 11.5 8.3125 11.5 6.5ZM10.5312 11.625C9.40625 12.5 8 13 6.5 13C2.90625 13 0 10.0938 0 6.5C0 2.9375 2.90625 0 6.5 0C10.0625 0 13 2.9375 13 6.5C13 8.03125 12.4688 9.4375 11.5938 10.5625L15.7812 14.7188C16.0625 15.0312 16.0625 15.5 15.7812 15.7812C15.4688 16.0938 15 16.0938 14.7188 15.7812L10.5312 11.625Z" />
                                </svg>
                            )}
                        </div>
                        <button
                            className="px-12 py-8 flex items-center space-x-8 rounded-8 bg-darwin-accent-bright-blue-800 text-white"
                            onClick={() => createTreatmentPackageModalToggleHandler()}
                        >
                            <p className="text-13 font-700">Create new</p>
                            <p className="p-4">
                                <svg
                                    className="w-12 h-12 fill-white"
                                    viewBox="0 0 12 12"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M6.60938 1.45312V5.51562H10.6719C11.002 5.51562 11.2812 5.79492 11.2812 6.125C11.2812 6.48047 11.002 6.73438 10.6719 6.73438H6.60938V10.7969C6.60938 11.1523 6.33008 11.4062 6 11.4062C5.64453 11.4062 5.39062 11.1523 5.39062 10.7969V6.73438H1.32812C0.972656 6.73438 0.71875 6.48047 0.71875 6.125C0.71875 5.79492 0.972656 5.51562 1.32812 5.51562H5.39062V1.45312C5.39062 1.12305 5.64453 0.84375 6 0.84375C6.33008 0.84375 6.60938 1.12305 6.60938 1.45312Z" />
                                </svg>
                            </p>
                        </button>
                    </div>
                </div>
                <div className="relative flex-1 w-full table-fader border border-neutral-7 overflow-hidden">
                    <div className="absolute inset-0 overflow-y-auto">
                        {renderComponent(filteredTreatmentPackages)}
                    </div>
                </div>

                <AnimatePresence>
                    {isCreateTreatmentPackageModalVisible && (
                        <CreateOrEditTreatmentPackage
                            closeMe={createTreatmentPackageModalToggleHandler}
                            editTreatmentPackage={editTreatmentPackage}
                            clinic_id={selectedClinicId}
                        />
                    )}
                </AnimatePresence>
            </div>
        );
    }
}

export default connect()(withRouter(ManageTreatmentPackages));
